import { isEntryOfContentType } from "@/lib/contentful/guards";
import type {
  TypeComparisonTableColumnHeader,
  TypeComparisonTableRow,
  TypeComparisonTableValue,
} from "@/types/contentful/generated";

type ColumnHeaderEntry = TypeComparisonTableColumnHeader<undefined, string>;
type RowEntry = TypeComparisonTableRow<undefined, string>;
type ValueEntry = TypeComparisonTableValue<undefined, string>;

type Props = {
  type: "company" | "product";
  title?: string;
  subtitle?: string;
  columnHeaders: ColumnHeaderEntry[];
  rows: RowEntry[];
};

const LABELS = {
  productName: ["product", "product name", "plan", "platform", "solution"],
  description: ["description", "overview"],
  price: ["price", "starting price", "cost"],
  priceCurrency: ["price currency", "currency"],
  category: ["category", "type"],
  url: ["url", "website", "link"],
  founder: ["founder"],
  knowsAbout: ["knows about", "expertise", "specialty", "speciality"],
};

function normalizeLabel(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, " ");
}

function parsePrice(value?: string): string | undefined {
  if (!value) return undefined;
  const match = value.match(/\d+(?:[.,]\d+)?/);
  if (!match) return undefined;
  return match[0].replace(/,/g, "");
}

function inferCurrency(priceValue?: string): string | undefined {
  if (!priceValue) return undefined;
  if (priceValue.includes("$")) return "USD";
  return undefined;
}

function parseCurrency(value?: string): string | undefined {
  if (!value) return undefined;
  const normalized = value.trim().toUpperCase();
  if (/^[A-Z]{3}$/.test(normalized)) return normalized;
  return undefined;
}

function parseAbsoluteUrl(value?: string): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol === "http:" || parsed.protocol === "https:") {
      return trimmed;
    }
  } catch {
    return undefined;
  }
  return undefined;
}

function getValueForColumn(rows: RowEntry[], columnId: string, labels: string[]): string | undefined {
  for (const row of rows) {
    const heading = normalizeLabel(row.fields.heading);
    if (!labels.includes(heading)) continue;

    const values = row.fields.values.filter((value): value is ValueEntry =>
      isEntryOfContentType<ValueEntry>(value, "comparisonTableValue"),
    );

    const match = values.find(value => {
      return isEntryOfContentType<ColumnHeaderEntry>(value.fields.column, "comparisonTableColumnHeader")
        ? value.fields.column.sys.id === columnId
        : false;
    });

    const resolvedValue = match?.fields.value.trim();
    if (resolvedValue) return resolvedValue;
  }

  return undefined;
}

function buildTableNode(title?: string, subtitle?: string) {
  const tableLabel = title?.trim() || subtitle?.trim() || "Comparison Table";
  return {
    "@type": "Table",
    about: {
      "@type": "Thing",
      name: tableLabel,
    },
    ...(subtitle?.trim() ? { description: subtitle.trim() } : {}),
  };
}

type OrganizationDetails = {
  url?: string;
  knowsAbout?: string;
  founder?: string;
};

function getOrganizationDetails(column: ColumnHeaderEntry, rows: RowEntry[]): OrganizationDetails {
  return {
    url: parseAbsoluteUrl(getValueForColumn(rows, column.sys.id, LABELS.url)),
    knowsAbout: getValueForColumn(rows, column.sys.id, LABELS.knowsAbout),
    founder: getValueForColumn(rows, column.sys.id, LABELS.founder),
  };
}

function hasOrganizationSignals(details: OrganizationDetails) {
  return Boolean(details.url || details.knowsAbout || details.founder);
}

function buildOrganizationNode(column: ColumnHeaderEntry, details: OrganizationDetails) {
  const companyId = `#company-${column.sys.id}`;

  return {
    "@type": "Organization",
    "@id": companyId,
    name: column.fields.name,
    ...(details.url ? { url: details.url } : {}),
    ...(details.knowsAbout ? { knowsAbout: details.knowsAbout } : {}),
    ...(details.founder ? { founder: details.founder } : {}),
  };
}

type ProductDetails = {
  productName?: string;
  productDescription?: string;
  price?: string;
  priceCurrency?: string;
  category?: string;
};

function getProductDetails(column: ColumnHeaderEntry, rows: RowEntry[]): ProductDetails {
  const productName = getValueForColumn(rows, column.sys.id, LABELS.productName);
  const productDescription = getValueForColumn(rows, column.sys.id, LABELS.description);
  const rawPrice = getValueForColumn(rows, column.sys.id, LABELS.price);
  const price = parsePrice(rawPrice);
  const priceCurrency =
    parseCurrency(getValueForColumn(rows, column.sys.id, LABELS.priceCurrency)) || inferCurrency(rawPrice);
  const category = getValueForColumn(rows, column.sys.id, LABELS.category);

  return {
    productName,
    productDescription,
    price,
    priceCurrency,
    category,
  };
}

function hasProductSignals(details: ProductDetails) {
  return Boolean(
    details.productName || details.productDescription || details.price || details.priceCurrency || details.category,
  );
}

function buildOffers(details: ProductDetails) {
  if (!details.price && !details.priceCurrency && !details.category) {
    return undefined;
  }

  return {
    "@type": "Offer",
    ...(details.price ? { price: details.price } : {}),
    ...(details.priceCurrency ? { priceCurrency: details.priceCurrency } : {}),
    ...(details.category ? { category: details.category } : {}),
  };
}

function buildProductNode(column: ColumnHeaderEntry, rows: RowEntry[], manufacturerId?: string) {
  const details = getProductDetails(column, rows);
  if (!hasProductSignals(details)) return null;

  const offers = buildOffers(details);

  return {
    "@type": "Product",
    "@id": `#product-${column.sys.id}`,
    name: details.productName || column.fields.name,
    ...(manufacturerId
      ? {
          manufacturer: {
            "@id": manufacturerId,
          },
        }
      : {}),
    ...(details.productDescription ? { description: details.productDescription } : {}),
    ...(offers ? { offers } : {}),
  };
}

function buildColumnGraphNodes(type: Props["type"], column: ColumnHeaderEntry, rows: RowEntry[]) {
  const nodes: unknown[] = [];
  const organizationDetails = getOrganizationDetails(column, rows);
  const companyId = `#company-${column.sys.id}`;
  const shouldIncludeOrganization = type === "company" || hasOrganizationSignals(organizationDetails);

  if (shouldIncludeOrganization) {
    nodes.push(buildOrganizationNode(column, organizationDetails));
  }

  if (type === "product") {
    const productNode = buildProductNode(column, rows, shouldIncludeOrganization ? companyId : undefined);
    if (productNode) {
      nodes.push(productNode);
    }
  }

  return nodes;
}

export function buildComparisonTableJsonLd({ type, title, subtitle, columnHeaders, rows }: Props) {
  if (columnHeaders.length === 0 || rows.length === 0) return null;

  const graph: unknown[] = [buildTableNode(title, subtitle)];

  for (const column of columnHeaders) {
    graph.push(...buildColumnGraphNodes(type, column, rows));
  }

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  };
}
